import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  FileStatus,
  FileUploaderFile,
  FileUploaderUploadEvent,
  UploadEvent,
  ValidDataTypes,
} from '../uploaders.interfaces';

@Injectable()
/**
 * Service to share the upload events between draggable zones
 * and inputs
 */
export class UploadEventsService {
  public events$: Observable<FileUploaderUploadEvent>;

  private _uploads$: Subject<Observable<UploadEvent>[]>;

  constructor() {
    this._uploads$ = new Subject();

    this.events$ = this._uploads$.pipe(
      switchMap(obs => this.mergeIndividualUploads(obs)),
    );
  }

  // tslint:disable-next-line:max-func-body-length
  public readFiles(
    fileList: FileList | null,
    dataType: ValidDataTypes = ValidDataTypes.None,
  ) {
    const fileObservables: Observable<UploadEvent>[] = [];
    if (fileList) {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < fileList.length; i += 1) {
        fileObservables.push(this.fileReaderObsFactory(fileList[i], dataType));
      }
    }
    this._uploads$.next(fileObservables);
  }

  private mergeIndividualUploads(obs: Observable<UploadEvent>[]) {
    return new Observable<FileUploaderUploadEvent>(observer => {
      const subs = combineLatest(obs).subscribe({
        next: events => {
          let aborted = false;
          let completedCount = 0;

          const next: FileUploaderUploadEvent = {
            files: events,
            status: FileStatus.EMPTY,
            error: null,
          };

          eventLoop: for (const event of events) {
            switch (event.status) {
              case FileStatus.ABORT:
                next.status = FileStatus.ABORT;
                aborted = true;
                break eventLoop;
              case FileStatus.LOADING:
                next.status = FileStatus.LOADING;
                break;
              case FileStatus.DONE:
                completedCount += 1;
                break;
              default:
            }
          }

          if (aborted) {
            for (const event of events) {
              if (event.reader) {
                event.reader.abort();
              }
            }
          } else if (completedCount === events.length) {
            next.status = FileStatus.DONE;
          }

          observer.next(next);
        },
        complete: () => observer.complete(),
        error: (err: UploadEvent) => observer.error(err),
      });

      return () => {
        subs.unsubscribe();
      };
    });
  }

  // tslint:disable-next-line:max-func-body-length
  private fileReaderObsFactory(file: File, dataType: ValidDataTypes) {
    const fileData: FileUploaderFile = {
      name: file.name,
      size: file.size,
      type: file.type,
      data: null,
      lastModified: file.lastModified,
      total: file.size,
      loaded: 0,
    };

    if (dataType === ValidDataTypes.None) {
      fileData.data = file;

      return of(this.eventFactory(fileData, FileStatus.DONE, null));
    }

    // tslint:disable-next-line:max-func-body-length
    return new Observable<UploadEvent>(observer => {
      let completed = false;

      const reader = new FileReader();

      reader.addEventListener(
        'error',
        () => {
          observer.error(this.eventFactory(fileData, FileStatus.ERROR, reader));
          completed = true;
          observer.complete();
        },
        { once: true },
      );

      reader.addEventListener(
        'abort',
        () => {
          observer.next(this.eventFactory(fileData, FileStatus.ABORT, reader));
          completed = true;
          observer.complete();
        },
        { once: true },
      );

      const onprogress = (e: ProgressEvent) => {
        if (e.lengthComputable) {
          fileData.total = e.total;
          fileData.loaded = e.loaded;
        }
        observer.next(this.eventFactory(fileData, FileStatus.LOADING, reader));
      };

      reader.addEventListener(
        'loadstart',
        () => {
          observer.next(
            this.eventFactory(fileData, FileStatus.LOADING, reader),
          );
        },
        { once: true },
      );

      reader.addEventListener('load', () => {
        fileData.loaded = fileData.size as number;
        fileData.data = reader.result;
        observer.next(this.eventFactory(fileData, FileStatus.DONE, reader));

        completed = true;
        observer.complete();
      });

      switch (dataType) {
        case ValidDataTypes.ArrayBuffer:
          reader.readAsArrayBuffer(file);
          break;
        case ValidDataTypes.DataURL:
          reader.readAsDataURL(file);
          break;
        case ValidDataTypes.Text:
          reader.readAsText(file);
          break;
        default:
          observer.error(
            new TypeError(
              'Invalid output type: Select ArrayBuffer, DataURL or Text',
            ),
          );
          completed = true;
          observer.complete();
          return;
      }

      return () => {
        if (reader) {
          if (!completed) {
            reader.abort();
          }

          if (onprogress) {
            reader.removeEventListener('progress', onprogress);
          }
        }
      };
    });
  }

  private eventFactory(
    file: FileUploaderFile,
    status: FileStatus,
    reader: FileReader | null = null,
  ) {
    return {
      status,
      reader,
      file: { ...file },
    };
  }
}
